import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  parseSearchWith,
  redirect,
  stringifySearchWith,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { LoadingScreen } from "./components/LoadingScreen";
import { NotFound, RouteError } from "./components/RouteStates";
import {
  filtersFromSearch,
  type CompletedAgeBucket,
  type SignoffStatus,
} from "./lib/board";
import {
  archivedInfiniteOptions,
  boardColumnOptions,
  ensureSession,
  notificationsInfiniteOptions,
} from "./lib/queries";
import { queryClient } from "./lib/queryClient";
import { Archived } from "./pages/Archived";
import { Dashboard } from "./pages/Dashboard";
import { Login } from "./pages/Login";
import { Notifications } from "./pages/Notifications";
import { Settings } from "./pages/Settings";

const DEV = process.env.NODE_ENV !== "production";

// Lazy + DEV-gated so the devtools are tree-shaken out of production builds.
// Flip SHOW_DEVTOOLS to re-enable the floating panel in development.
const SHOW_DEVTOOLS = false;
const RouterDevtools =
  DEV && SHOW_DEVTOOLS
    ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  return (
    <>
      <Outlet />
      <Suspense>
        <RouterDevtools />
      </Suspense>
    </>
  );
}

/** Guard shared by every authenticated route: no session → bounce to /login. */
async function requireSession({ context }: { context: RouterContext }) {
  const user = await ensureSession(context.queryClient);
  if (!user) throw redirect({ to: "/login" });
}

const BOARD_STATUSES: SignoffStatus[] = [
  "pending_review",
  "changes_requested",
  "accepted",
];

const COMPLETED_AGE_BUCKETS: CompletedAgeBucket[] = [
  "24h",
  "7d",
  "30d",
  "90d",
  "180d",
];

type IndexSearch = {
  task?: string;
  space_id?: string;
  lists?: string[];
  assignees?: string[];
  creators?: string[];
  completedAge?: CompletedAgeBucket;
  priorities?: number[];
  q?: string;
};

const stringArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const xs = v.filter((x): x is string => typeof x === "string");
  return xs.length ? xs : undefined;
};

const numberArray = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const xs = v
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 3);
  return xs.length ? xs : undefined;
};

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>): IndexSearch => ({
    task: typeof search.task === "string" ? search.task : undefined,
    // Coerce to string: the clean (unquoted) URL means a numeric-looking id
    // decodes as a number, so accept either and normalize.
    space_id: search.space_id != null ? String(search.space_id) : undefined,
    lists: stringArray(search.lists),
    assignees: stringArray(search.assignees),
    creators: stringArray(search.creators),
    completedAge: COMPLETED_AGE_BUCKETS.includes(
      search.completedAge as CompletedAgeBucket,
    )
      ? (search.completedAge as CompletedAgeBucket)
      : undefined,
    priorities: numberArray(search.priorities),
    q:
      search.q != null && String(search.q).trim()
        ? String(search.q)
        : undefined,
  }),
  // Only the filter params drive the column fetches; `task` (the modal) doesn't,
  // so it's excluded — opening a task won't refire the loader.
  loaderDeps: ({ search }) => ({
    space_id: search.space_id,
    lists: search.lists,
    assignees: search.assignees,
    creators: search.creators,
    completedAge: search.completedAge,
    priorities: search.priorities,
    q: search.q,
  }),
  beforeLoad: requireSession,
  // Kick off the column fetches before the component mounts (no await → nav
  // stays instant; useInfiniteQuery attaches to the in-flight requests). With
  // defaultPreload:"intent" this even starts on link hover.
  loader: ({ context, deps }) => {
    if (!deps.space_id) return; // no space yet → the board redirects to a default
    const filters = filtersFromSearch(deps);
    for (const status of BOARD_STATUSES) {
      void context.queryClient.ensureInfiniteQueryData(
        boardColumnOptions(status, deps.space_id, filters),
      );
    }
  },
  component: Dashboard,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  beforeLoad: async ({ context }) => {
    // Already signed in? Skip the login screen.
    const user = await ensureSession(context.queryClient);
    if (user) throw redirect({ to: "/" });
  },
  component: Login,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: requireSession,
  component: Settings,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  beforeLoad: requireSession,
  loader: ({ context }) => {
    void context.queryClient.ensureInfiniteQueryData(
      notificationsInfiniteOptions(),
    );
  },
  component: Notifications,
});

const archivedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/archived",
  validateSearch: (
    search: Record<string, unknown>,
  ): { task?: string; space_id?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
    space_id: search.space_id != null ? String(search.space_id) : undefined,
  }),
  loaderDeps: ({ search }) => ({ space_id: search.space_id }),
  beforeLoad: requireSession,
  loader: ({ context, deps }) => {
    if (!deps.space_id) return;
    void context.queryClient.ensureInfiniteQueryData(
      archivedInfiniteOptions("", deps.space_id),
    );
  },
  component: Archived,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  settingsRoute,
  notificationsRoute,
  archivedRoute,
]);

// Keep search params factual in the URL: scalar strings stay raw (so a
// numeric-looking id reads as ?space_id=32463561, not ?space_id=%2232463561%22)
// while arrays/objects still round-trip as JSON. validateSearch already treats
// every value as a string/array, so dropping JSON's type-preserving quotes is safe.
const parseSearch = parseSearchWith((value) => {
  const parsed = JSON.parse(value);
  return parsed !== null && typeof parsed === "object" ? parsed : value;
});
const stringifySearch = stringifySearchWith(JSON.stringify);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  parseSearch,
  stringifySearch,
  // Show the branded loader immediately while route guards resolve the session.
  defaultPendingComponent: LoadingScreen,
  defaultPendingMs: 0,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: RouteError,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
